import { Box, breadcrumbsClasses, Tab, Tabs } from '@mui/material';
import './AccountSettings.css';
import { useState } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { auth, db } from '../../../firebase/firebase_setup';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import { SettingsPowerRounded } from '@mui/icons-material';
import { getFunctions, httpsCallable } from 'firebase/functions';


export function AccountSettings({userData, setSettingsOpen, accentColour}) {

  const [tab, setTab] = useState('name');

  const [firstName, setFirstName] = useState(userData.name);
  const [lastName, setLastName] = useState(userData.last_name ?? '');
  const [nameEdited, setNameEdited] = useState(false);

  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [emailEdited, setEmailEdited] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordEdited, setPasswordEdited] = useState(false);

  const [working, setWorking] = useState(false);

  const handleChange = (e) => {
    setTab(e.target.id);
  }

  const handleNameUpdate = async() => {
    setWorking(true);
    if(!firstName.trim().length || !lastName.trim().length) {
      alert('Please provide both a first and last name');
      setWorking(false);
      return;
    }
    try {
      const docRef = doc(db, 'users', userData.uid);
      await setDoc(docRef, {name: firstName.trim(), last_name: lastName.trim()}, {merge: true});
      alert('Name updated successfully')
      window.location.reload();
    }
    catch(err) {
      console.error(err);
      alert('Could not update name. Please try again');
    }
    finally {
      setWorking(false);
    }
  }

  const handleUpdateEmail = async() => {
    setWorking(true);

    if(!newEmail.trim().length || !emailPassword.trim().length) {
      alert('Please provide all fields');
      setWorking(false);
      return;
    }
    
    try {
      await changeEmail(emailPassword, newEmail);
      alert("We've sent a verification email to your new address. Your email will be updated once you've verified it");
    }
    catch(err) {
      switch (err.code) {
        case "functions/already-exists":
          alert("That email address is already in use");
          break;

        case "functions/invalid-argument":
          alert(
            err.message || "Please enter a valid email address"
          );
          break;

        case "auth/invalid-credential":
        case "auth/wrong-password":
          alert("Your password is incorrect");
          break;

        case "auth/requires-recent-login":
          alert(
            "Please sign out and sign back in before changing your email"
          );
          break;

        default:
          console.error(err);
          alert("Could not update your email");
      }
    }
    finally {
      setWorking(false);
    }
  }

  const handleUpdatePassword = async() => {
    if(!currentPassword.trim() || !newPassword.trim() || !confirmPassword.trim()) {
      alert('Please provide all fields');
      return;
    }
    if(newPassword !== confirmPassword) {
      alert('Your new passwords do not match');
      return
    }
    if(newPassword.length < 8) {
      alert('Your new password must be at least 8 characters long');
      return;
    }

    setWorking(true);

    try {
      await changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordEdited(false);
      alert('Password updated successfully');
    }
    catch(err) {
      console.error(err);
      switch (err.code) {
        case 'auth/invalid-credential':
        case 'auth/wrong-password':
          alert('Your current password is incorrect');
          break;
        case 'auth/weak-password':
          alert('Your new password is too weak');
          break;
        case 'auth/requires-recent-login':
          alert('Please sign out and sign back in before changing your password');
          break;
        case 'auth/passwords-match':
          alert('Your new password must be different from your current password');
          break;
        default:
          alert('Could not update your password');
      }
    }
    finally {
      setWorking(false);
    }
  }

  return (
    <div className='account-settings-container' onClick={() => setSettingsOpen(false)}>
      <div className='account-settings-menu' onClick={(e) => e.stopPropagation()}>
        <h3>Account Settings</h3>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tab} onChange={handleChange}>
            <Tab label='Name' value='name' id='name' />
            <Tab label='Email' value='email' id='email' />
            <Tab label='Password' value='password' id='password' />
          </Tabs>
        </Box>

        <div className='account-settings-tab'>
        {
          tab === 'name' 
          ? <form onSubmit={(e) => {e.preventDefault(); handleNameUpdate()}}>
              <div className='account-settings-two-col'>
                <div>
                  <p className='account-settings-label'>First Name:</p>
                  <input value={firstName} onChange={(e) => {setFirstName(e.target.value); setNameEdited(true)}} style={{backgroundColor: '#0a0a0a'}} />
                </div>
                <div>
                  <p className='account-settings-label'>Last Name:</p>
                  <input value={lastName} onChange={(e) => {setLastName(e.target.value); setNameEdited(true)}} style={{backgroundColor: '#0a0a0a'}} />
                </div>
              </div>
              <button type='submit' disabled={!nameEdited || working} style={{marginTop: '20px', backgroundColor: (nameEdited && !working) ? accentColour : '#555'}}>Update</button>
            </form>
          : 
          tab === 'email' 
          ? <form onSubmit={(e) => {e.preventDefault(); handleUpdateEmail()}}>
              <div className='account-settings-one-col'>
                <p className='account-settings-label'>Current Email:</p>
                <input disabled={true} value={userData.email} style={{backgroundColor: '#0a0a0a'}} />
              </div>
              <div className='account-settings-one-col'>
                <p className='account-settings-label'>New Email:</p>
                <input value={newEmail} onChange={(e) => {setNewEmail(e.target.value); setEmailEdited(true)}} style={{backgroundColor: '#0a0a0a'}} />
              </div>
              <div className='account-settings-one-col'>
                <p className='account-settings-label'>Password:</p>
                <input type='password' value={emailPassword} onChange={(e) => {setEmailPassword(e.target.value); setEmailEdited(true)}} style={{backgroundColor: '#0a0a0a'}} />
              </div>
              <div className='account-settings-one-col'>
                <div style={{display: 'flex', gap: '20px', alignItems: 'center'}}>
                  <button style={{flexShrink: 0}} type='submit' disabled={!emailEdited || working} style={{marginTop: '20px', backgroundColor: (emailEdited && !working) ? accentColour : '#555'}}>Change Email</button>
                  <p className='disclaimer-text'>To finish changing your email, you will need to confirm via the new email address.</p>
                </div>
              </div>
            </form>

          : <form onSubmit={(e) => {e.preventDefault(); handleUpdatePassword()}}>
              <div className='account-settings-one-col'>
                <p className='account-settings-label'>Current Password:</p>
                <input type='password' value={currentPassword} onChange={(e) => {setCurrentPassword(e.target.value); setPasswordEdited(true);}} style={{backgroundColor: '#0a0a0a'}} />
              </div>
              <div className='account-settings-one-col'>
                <p className='account-settings-label'>New Password:</p>
                <input type='password' value={newPassword} onChange={(e) => {setNewPassword(e.target.value); setPasswordEdited(true)}} style={{backgroundColor: '#0a0a0a'}} />
              </div>
              <div className='account-settings-one-col'>
                <p className='account-settings-label'>Confirm New Password:</p>
                <input type='password' value={confirmPassword} onChange={(e) => {setConfirmPassword(e.target.value); setPasswordEdited(true)}} style={{backgroundColor: '#0a0a0a'}} />
              </div>
              <button type='submit' disabled={!passwordEdited || working} style={{marginTop: '20px', backgroundColor: (passwordEdited && !working) ? accentColour : '#555'}}>Change Password</button>
            </form>
        }
        </div>
      </div>
    </div>
  )

}



async function changeEmail(currentPassword, newEmail) {
  const user = auth.currentUser;

  if (!user || !user.email) {
    throw new Error("No signed-in email/password user");
  }

  const credential = EmailAuthProvider.credential(
    user.email,
    currentPassword
  );

  await reauthenticateWithCredential(user, credential);

  const functions = getFunctions();
  const requestEmailChange = httpsCallable(
    functions, 'requestEmailChange'
  )
  return requestEmailChange({newEmail: newEmail.trim()})
}


async function changePassword(currentPassword, newPassword) {
  const user = auth.currentUser;

  if(!user?.email) {
    throw new Error('No signed-in user');
  }

  const credential = EmailAuthProvider.credential(user.email, currentPassword);

  await reauthenticateWithCredential(user, credential);

  if(currentPassword === newPassword) {
    throw {
      code: 'auth/passwords-match',
      message: 'The new password must be different from the current password'
    };
  }

  await updatePassword(user, newPassword);
}